import React from 'react';
import {
  Filter,
  Download,
  FileSpreadsheet,
  FileCode2,
  ChevronDown,
  RefreshCw,
  X,
  CheckSquare,
} from 'lucide-react';
import { VisibilityState } from '@tanstack/react-table';
import { DataTableColumn, DensityMode, FilterRule } from '../../../types/grid';
import { FilterSearchBar } from '../FilterSearchBar';
import { ColumnVisibilityMenu } from '../ColumnVisibilityMenu';
import { DensityToggle } from '../DensityToggle';
import { PortalPopover } from '../../ui/PortalPopover';
import { Button } from '../../ui/Button';
import { BulkAction } from './types';

interface DataTableToolbarProps<T extends Record<string, any>> {
  columns: DataTableColumn<T>[];
  activeFilterRules: FilterRule[];
  onFilterRulesChange: (rules: FilterRule[]) => void;
  globalFilter: string;
  onSearchChange: (search: string) => void;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  visibilityColumnsList: Array<{ key: string; name: string; isSystem: boolean }>;
  columnVisibility: VisibilityState;
  setColumnVisibility: (updaterOrValue: any) => void;
  selectedRowIds: string[];
  onClearSelection: () => void;
  bulkActions?: BulkAction<T>[];
  selectedRows: T[];
  onRefresh?: () => void;
  isRefreshing?: boolean;
  isLoading?: boolean;
  density: DensityMode;
  setDensity: (d: DensityMode) => void;
  exportOpen: boolean;
  setExportOpen: (open: boolean) => void;
  isExporting: boolean;
  exportButtonRef: React.RefObject<HTMLButtonElement | null>;
  onExport: (format: 'csv' | 'json', targetRows?: T[]) => Promise<void>;
  customToolbarActions?: React.ReactNode;
  primaryAction?: {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
  };
}

export function DataTableToolbar<T extends Record<string, any>>({
  columns,
  activeFilterRules,
  onFilterRulesChange,
  globalFilter,
  onSearchChange,
  searchInputRef,
  visibilityColumnsList,
  columnVisibility,
  setColumnVisibility,
  selectedRowIds,
  onClearSelection,
  bulkActions,
  selectedRows,
  onRefresh,
  isRefreshing = false,
  isLoading = false,
  density,
  setDensity,
  exportOpen,
  setExportOpen,
  isExporting,
  exportButtonRef,
  onExport,
  customToolbarActions,
  primaryAction,
}: DataTableToolbarProps<T>) {
  return (
    <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-2 p-2 bg-cf-tableHeader dark:bg-[#20232a] border-b border-cf-border dark:border-[#2e323c]">
      {/* Tier 1 on Mobile / Left Group on Desktop: Search Bar & Columns */}
      <div className="flex items-center gap-1.5 flex-1 min-w-0 w-full md:w-auto">
        <div className="flex-1 min-w-0">
          <FilterSearchBar
            columns={columns}
            filterRules={activeFilterRules}
            onFilterRulesChange={onFilterRulesChange}
            searchValue={globalFilter}
            onSearchChange={onSearchChange}
            inputRef={searchInputRef}
            placeholder="Search or filter by column... (/)"
          />
        </div>

        {/* Desktop Columns */}
        <div className="hidden md:flex items-center gap-1.5 shrink-0">
          <ColumnVisibilityMenu
            columns={visibilityColumnsList}
            visibility={columnVisibility}
            onChange={setColumnVisibility}
          />
        </div>

        {/* Mobile Primary Action Button */}
        {primaryAction && (
          <Button
            variant="primary"
            size="sm"
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled}
            icon={primaryAction.icon}
            className="md:hidden shrink-0 shadow-xs"
          >
            <span className="whitespace-nowrap">{primaryAction.label}</span>
          </Button>
        )}
      </div>

      {/* Tier 2 on Mobile (Smooth Horizontal Scroll Strip) / Right Group on Desktop */}
      <div className="flex items-center gap-1.5 w-full md:w-auto overflow-x-auto no-scrollbar py-0.5 md:py-0 shrink-0">
        {/* Mobile Active Filter Clear Pill */}
        {activeFilterRules.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() => onFilterRulesChange([])}
            icon={<Filter size={11} />}
            className="md:hidden bg-cf-orange/10 border border-cf-orange/30 text-cf-orange hover:bg-cf-orange/20 shrink-0 font-medium"
            title="Clear all active column filters"
          >
            <span>Filters ({activeFilterRules.length})</span>
            <X size={11} className="hover:text-cf-textPrimary ml-0.5" />
          </Button>
        )}

        {/* Mobile Columns */}
        <div className="flex md:hidden items-center gap-1.5 shrink-0">
          <ColumnVisibilityMenu
            columns={visibilityColumnsList}
            visibility={columnVisibility}
            onChange={setColumnVisibility}
          />
        </div>

        {/* Selection Counter & Bulk Actions */}
        {selectedRowIds.length > 0 && (
          <div className="flex items-center gap-1.5 shrink-0">
            <div className="flex items-center gap-1.5 px-2 h-7 bg-cf-orange/10 border border-cf-orange/30 rounded text-cf-orange text-xs font-medium">
              <CheckSquare size={12} />
              <span>{selectedRowIds.length}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={onClearSelection}
                className="h-4 w-4 p-0 hover:text-cf-textPrimary ml-0.5"
                title="Deselect all (Esc)"
              >
                <X size={12} />
              </Button>
            </div>

            {bulkActions && bulkActions.length > 0 && (
              <div className="flex items-center gap-1.5">
                {bulkActions.map((action, idx) => (
                  <Button
                    key={idx}
                    type="button"
                    variant={action.variant === 'danger' ? 'danger' : 'outline'}
                    size="xs"
                    onClick={() => action.onClick(selectedRows)}
                    icon={action.icon}
                  >
                    <span>{action.label}</span>
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Refresh Button */}
        {onRefresh && (
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={onRefresh}
            disabled={isRefreshing || isLoading}
            icon={<RefreshCw size={12} className={isRefreshing ? 'animate-spin text-cf-orange' : ''} />}
            className="shrink-0"
            title="Refresh table data"
          >
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        )}

        {/* Density Toggle */}
        <div className="shrink-0">
          <DensityToggle
            density={density}
            onChange={setDensity}
          />
        </div>

        {/* Export Menu */}
        <div className="relative shrink-0">
          <Button
            ref={exportButtonRef as React.RefObject<HTMLButtonElement>}
            type="button"
            variant="outline"
            size="xs"
            onClick={() => setExportOpen(!exportOpen)}
            disabled={isExporting}
            icon={<Download size={12} />}
            rightIcon={<ChevronDown size={10} className="text-cf-textMuted" />}
            title="Export table data"
          >
            <span className="hidden sm:inline">Export</span>
          </Button>

          <PortalPopover
            isOpen={exportOpen}
            onClose={() => setExportOpen(false)}
            triggerRef={exportButtonRef as React.RefObject<HTMLElement>}
            align="right"
            className="w-48 bg-cf-surface border border-cf-border rounded-lg shadow-xl py-1 text-xs font-sans"
          >
            <div className="px-3 py-1 text-xs text-cf-textMuted uppercase tracking-wider">
              {selectedRowIds.length > 0 ? `Export Selected (${selectedRowIds.length})` : 'Export Filtered'}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onExport('csv', selectedRowIds.length > 0 ? selectedRows : undefined)}
              icon={<FileSpreadsheet size={13} className="text-cf-success" />}
              className="w-full justify-start rounded-none px-3"
            >
              <span>CSV Spreadsheet</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onExport('json', selectedRowIds.length > 0 ? selectedRows : undefined)}
              icon={<FileCode2 size={13} className="text-cf-info" />}
              className="w-full justify-start rounded-none px-3"
            >
              <span>JSON Payload</span>
            </Button>
          </PortalPopover>
        </div>

        {/* Custom Toolbar Actions Slot */}
        {customToolbarActions && <div className="shrink-0">{customToolbarActions}</div>}

        {/* Desktop Primary Action Button */}
        {primaryAction && (
          <Button
            variant="primary"
            size="xs"
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled}
            icon={primaryAction.icon}
            className="hidden md:flex shrink-0 shadow-xs"
          >
            <span>{primaryAction.label}</span>
          </Button>
        )}
      </div>
    </div>
  );
}
